import './setup_custom_elements';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';

// Silence specific warnings in dev mode for complete White Label experience
/* eslint-disable no-console */
const originalWarn = console.warn;
console.warn = function suppressKnownDashboardWarnings(...args) {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('Lit is in dev mode') || args[0].includes('lumeratec'))
  ) {
    return;
  }
  originalWarn.apply(console, args);
};

// Removed inline override because it is now handled in setup_custom_elements.js
import axios from 'axios';
// Global Components
import hljsVuePlugin from '@highlightjs/vue-plugin';

import { plugin, defaultConfig } from '@formkit/vue';
import WootWizard from 'components/ui/Wizard.vue';
import FloatingVue from 'floating-vue';
import WootUiKit from 'dashboard/components';
import App from 'dashboard/App.vue';
import i18nMessages from 'dashboard/i18n';
import createAxios from 'dashboard/helper/APIHelper';

import commonHelpers, { isJSONValid } from 'dashboard/helper/commons';
import { sync } from 'vuex-router-sync';
import { createPinia } from 'pinia';
import router, { initalizeRouter } from 'dashboard/routes';
import store from 'dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeLumeratecEvents,
} from 'dashboard/helper/scriptHelpers.js';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';

import { vResizeObserver } from '@vueuse/components';
import { directive as onClickaway } from 'vue3-click-away';

import 'floating-vue/dist/style.css';

const i18n = createI18n({
  legacy: false, // https://github.com/intlify/vue-i18n/issues/1902
  locale: 'en',
  messages: i18nMessages,
});

sync(store, router);

const pinia = createPinia();

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(pinia);
app.use(router);

// [VITE] Disabled this, need to renable later
const sentryConfig = window.sentryConfig || {};
const sentryDsn = sentryConfig.dsn || window.errorLoggingConfig;

if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: sentryConfig.environment,
    release: sentryConfig.release,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
    beforeSend(event) {
      event.tags = { ...event.tags, component: 'dashboard' };
      if (event.user) event.user = { id: event.user.id };
      if (event.request) {
        delete event.request.data;
        delete event.request.headers;
      }
      return event;
    },
  });
}

app.use(VueDOMPurifyHTML, domPurifyConfig);
app.use(WootUiKit);
app.use(
  plugin,
  defaultConfig({
    rules: {
      JSON: ({ value }) => isJSONValid(value),
    },
  })
);
app.use(FloatingVue, {
  instantMove: true,
  arrowOverflow: false,
  disposeTimeout: 5000000,
});
app.use(hljsVuePlugin);

app.component('woot-wizard', WootWizard);
app.component('fluent-icon', FluentIcon);

app.directive('resize', vResizeObserver);
app.directive('on-clickaway', onClickaway);

import moduleMixin from 'dashboard/mixins/moduleMixin';

app.mixin(moduleMixin);

// load common helpers into js
commonHelpers();
window.WootConstants = constants;
window.axios = createAxios(axios);
// [VITE] Disabled this we don't need it, we can use `useEmitter` directly
// app.prototype.$emitter = emitter;

initializeLumeratecEvents();
initializeAnalyticsEvents();
initalizeRouter();

window.onload = async () => {
  // The initial route guard validates the saved auth token asynchronously.
  // Waiting for it prevents protected views from mounting with an expired
  // session and firing API requests before the redirect to login happens.
  await router.isReady();
  app.mount('#app');
};
